import {
  lazy,
  Suspense,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useSelector } from 'react-redux';
import { CaretDown, CaretRight, Check } from '@phosphor-icons/react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { hapticsImpactLight } from '../../../../utils/haptics';
import { examsSelectors } from '../../../../slices/exams/examsSlice';
import { TriangleDown } from '../../../../assets/svgs/TriangleDown';
import type { RootState } from '../../../../store/store';
import type { UserExam } from '../../../../types/User';
import { useActivateExam } from '../../../../hooks/useActivateExam';
import { useUserExamUpdate } from '../../../../utils/updateUserExam';
import { usePrimaryExamType } from '../../../../hooks/usePrimaryExamType';
import { convertComlexLabel } from '../../../../utils/convertComlexLabel';
import {
  isComlexExam,
  isNclexExam,
  isResidencyExam,
  isShelfExam,
  PRECLINICAL_EXAM_ID,
} from '../../../../utils/examUtils';
import { useIsCourseStudent } from '../../../../instances/useInstance';
import { useMcGrawHillExamMenu } from '../../../../mcgrawHill/useMcGrawHillExamMenu'; // mcgraw-hill
import DutExamMenu from '../../../../mcgrawHill/DutExamMenu'; // mcgraw-hill

// A course student's selector (Courses V1), fetched only for them.
const CourseSelectors = lazy(() => import('./CourseSelectors'));

interface ExamSelectorsProps {
  /**
   * `card` — the stacked label/name float button the Dashboard header has
   * always used (below 900px, where there is no window header). `header` —
   * a compact pill for the OS window header and the desktop strip.
   */
  variant: 'card' | 'header';
}

// Mirrors the desktop quick-action chips so the pill reads as the same
// family of floating controls. Shared with the desktop's other floating
// pills (the Open Dashboard button) so they read as one set.
export const desktopPillClassName =
  'flex cursor-pointer items-center gap-2 rounded-full border border-border-subtle bg-surface-raised/60 px-4 py-1.5 font-heading text-sm text-fg-primary shadow-low backdrop-blur-xl transition-colors hover:bg-surface-highlight focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none';

/**
 * The exam (workspace) selector: the menu of the student's exams. One source
 * of truth for the menu — it used to be duplicated between the Dashboard
 * header and the nav rail's selector.
 */
const ExamSelectors = ({ variant }: ExamSelectorsProps) => {
  const currentUser = useSelector((state: RootState) => state.user.profile);
  const availableExams = useSelector(examsSelectors.selectAll);
  const stepExams = useSelector(examsSelectors.selectStepExams);
  const comlexExams = useSelector(examsSelectors.selectComlexExams);
  const shelfExams = useSelector(examsSelectors.selectShelfExams);
  const residencyExams = useSelector(examsSelectors.selectResidencyExams);
  const nclexExams = useSelector(examsSelectors.selectNclexExams);
  const { isPreclinical, primaryExam } = usePrimaryExamType();
  const isCourseStudent = useIsCourseStudent();
  const { activateExam } = useActivateExam();
  // mcgraw-hill (ORA-1247): a member's menu lists only their vertical's exams.
  const mcgrawHillMenu = useMcGrawHillExamMenu();

  const { updateExam } = useUserExamUpdate(undefined, {
    showSuccessMessage: false,
  });

  const [examMenuIsOpen, setExamMenuIsOpen] = useState(false);

  const [examSelection, setExamSelection] = useState<UserExam | null>(
    primaryExam || null
  );

  // The grouped exams live behind accordions (the nav rail's old workspace
  // menu); the group holding the active exam opens with the menu.
  const [isShelfExpanded, setIsShelfExpanded] = useState(false);
  const [isComlexExpanded, setIsComlexExpanded] = useState(false);
  const [isSubiExpanded, setIsSubiExpanded] = useState(false);
  const [isNursingExpanded, setIsNursingExpanded] = useState(false);

  useEffect(() => {
    if (!examMenuIsOpen || !primaryExam) return;
    if (isShelfExam(primaryExam)) setIsShelfExpanded(true);
    if (isComlexExam(primaryExam)) setIsComlexExpanded(true);
    if (isResidencyExam(primaryExam)) setIsSubiExpanded(true);
    if (isNclexExam(primaryExam)) setIsNursingExpanded(true);
  }, [examMenuIsOpen, primaryExam]);

  useEffect(() => {
    if (primaryExam && examSelection?.id !== primaryExam.id) {
      setExamSelection(primaryExam);
    }
  }, [primaryExam]);

  /**
   * Handles exam selection with email verification for school exams
   */
  const handleExamSelection = (exam: UserExam) => {
    setExamSelection(exam);
    setExamMenuIsOpen(false);
    void activateExam(exam);
  };

  const menuKey = useMemo(() => availableExams.length, [availableExams]);

  /**
   * Preclinical is a workspace, not an exam to choose from: the server keeps
   * the student's active custom exam, so switching lands back in the one they
   * were last studying (and seeds one when they have none). Picking a
   * different block is the preclinical dashboard's own control — making the
   * switch itself go through that picker, as this menu did, meant a student
   * could not simply return to where they left off.
   */
  const handlePreclinicalSelection = async () => {
    setExamMenuIsOpen(false);
    hapticsImpactLight();
    if (!currentUser) return;

    await updateExam({
      userId: currentUser.id,
      examId: PRECLINICAL_EXAM_ID,
      primary: true,
    });
  };

  // Preclinical names the workspace, not the block: which custom exam is
  // active is the preclinical dashboard's own heading, so repeating it here
  // said "Cardiology" in a control whose menu has no Cardiology in it.
  const examPillLabel =
    mcgrawHillMenu.pillLabel ??
    (isPreclinical
      ? 'Preclinical'
      : examSelection
        ? convertComlexLabel(examSelection.name)
        : 'Select Exam');

  const renderExamOption = (exam: UserExam) => {
    const label = isShelfExam(exam)
      ? exam.name.replace('Shelf Exam', '').trim()
      : convertComlexLabel(exam.name);

    return (
      <Button
        variant="ghost"
        key={'exam-' + exam.id}
        pressed={exam.id === examSelection?.id}
        context="menu"
        onClick={() => handleExamSelection(exam)}
        tabIndex={0}
        aria-label={`Select ${label}`}
      >
        {label}
        {exam.hasCompletedRealExam && (
          <Check aria-hidden="true" className="size-3" />
        )}
      </Button>
    );
  };

  const renderExamGroup = ({
    id,
    label,
    exams,
    isExpanded,
    onToggle,
    children,
  }: {
    id: string;
    label: string;
    exams?: UserExam[];
    isExpanded: boolean;
    onToggle: () => void;
    /** Options of the caller's own, in place of `exams`. */
    children?: ReactNode;
  }) => (
    <>
      <Button
        variant="ghost"
        pressed={isExpanded}
        context="menu"
        onClick={onToggle}
        tabIndex={0}
        aria-label={label}
        aria-expanded={isExpanded}
        aria-controls={id}
      >
        {isExpanded ? (
          <CaretDown weight="fill" className="size-2" />
        ) : (
          <CaretRight weight="fill" className="size-2" />
        )}
        <span>{label}</span>
      </Button>
      <div id={id} className="flex flex-col pl-4" hidden={!isExpanded}>
        {children ?? exams?.map(renderExamOption)}
      </div>
    </>
  );

  const triggerProps = {
    id: 'exam-menu-button',
    'aria-controls': examMenuIsOpen ? 'exam-menu' : undefined,
    'aria-haspopup': true as const,
    'aria-expanded': examMenuIsOpen ? ('true' as const) : undefined,
    'aria-label': 'Select Exam',
    onClick: () => {
      hapticsImpactLight();
    },
    tabIndex: 0,
  };

  // A partner-course instance picks between its courses, not exams.
  if (isCourseStudent) {
    return (
      <div
        className={cn(
          variant === 'card' && 'home-header_exam-selections_top',
          variant === 'header' && 'flex min-w-0 items-center gap-2'
        )}
      >
        <Suspense fallback={null}>
          <CourseSelectors />
        </Suspense>
      </div>
    );
  }

  return (
    <div
      className={cn(
        variant === 'card' && 'home-header_exam-selections_top',
        variant === 'header' && 'flex min-w-0 items-center gap-2'
      )}
    >
      <Popover open={examMenuIsOpen} onOpenChange={setExamMenuIsOpen}>
        <PopoverTrigger asChild>
          {variant === 'card' ? (
            <button
              className="button button--float button--exam-select"
              {...triggerProps}
            >
              <div>
                <span className="label">Exam</span>
                <span className="exam-name">
                  {mcgrawHillMenu.pillLabel ?? examSelection?.name}
                </span>
              </div>
              <TriangleDown />
            </button>
          ) : (
            <button
              className={desktopPillClassName}
              type="button"
              {...triggerProps}
            >
              <span className="truncate">{examPillLabel}</span>
              <CaretDown aria-hidden="true" size={12} weight="bold" />
            </button>
          )}
        </PopoverTrigger>
        <PopoverContent
          key={menuKey}
          id="exam-menu"
          className="max-h-[min(70dvh,var(--radix-popover-content-available-height))] w-80 overflow-y-auto"
          align="center"
          sideOffset={8}
        >
          <div className="flex flex-col gap-1">
            {mcgrawHillMenu.emptyLabel && (
              <p className="m-0 px-3 py-2 text-sm text-fg-secondary">
                {mcgrawHillMenu.emptyLabel}
              </p>
            )}
            {mcgrawHillMenu.hasDutCourses && (
              <DutExamMenu
                renderExamOption={renderExamOption}
                renderExamGroup={renderExamGroup}
                onClose={() => setExamMenuIsOpen(false)}
              />
            )}
            {mcgrawHillMenu.shows('preclinical') && (
              <Button
                variant="ghost"
                pressed={isPreclinical}
                context="menu"
                onClick={() => void handlePreclinicalSelection()}
                tabIndex={0}
                aria-label="Switch to Preclinical"
              >
                Preclinical
              </Button>
            )}
            {mcgrawHillMenu.shows('step') && stepExams.map(renderExamOption)}
            {mcgrawHillMenu.shows('shelf') &&
              renderExamGroup({
                id: 'shelf-exams-panel',
                label: 'Shelf Exams',
                exams: shelfExams,
                isExpanded: isShelfExpanded,
                onToggle: () => setIsShelfExpanded((value) => !value),
              })}
            {mcgrawHillMenu.shows('comlex') &&
              renderExamGroup({
                id: 'comlex-supplements-panel',
                label: 'COMLEX Supplements',
                exams: comlexExams,
                isExpanded: isComlexExpanded,
                onToggle: () => setIsComlexExpanded((value) => !value),
              })}
            {mcgrawHillMenu.shows('residency') &&
              residencyExams.length > 0 &&
              renderExamGroup({
                id: 'sub-internships-panel',
                label: 'Sub-Internships',
                exams: residencyExams,
                isExpanded: isSubiExpanded,
                onToggle: () => setIsSubiExpanded((value) => !value),
              })}
            {/* Nursing stays last and collapsed for a medical active exam;
                it renders only when the account holds an NCLEX exam. */}
            {mcgrawHillMenu.shows('nclex') &&
              nclexExams.length > 0 &&
              renderExamGroup({
                id: 'nursing-exams-panel',
                label: 'Nursing',
                exams: nclexExams,
                isExpanded: isNursingExpanded,
                onToggle: () => setIsNursingExpanded((value) => !value),
              })}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default ExamSelectors;
